import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import API_BASE, { withApiCredentials } from "../api";
import { getActiveAwareAuthToken } from "../auth/bootstrapTokens";
import { mergeUserPreservingNewerUsername } from "../auth/userFreshness";
import { trackProductEvent } from "../analytics/trackEvent";
import countries from "../data/countries";

const FISHING_METHODS = [
  "methods.boat_spinning",
  "methods.bottom_fishing",
  "methods.dead_bait",
  "methods.drift_fishing",
  "methods.feeder",
  "methods.fly_fishing",
  "methods.float_fishing",
  "methods.jigging",
  "methods.kayak_fishing",
  "methods.live_bait",
  "methods.other",
  "methods.popping",
  "methods.spinning",
  "methods.surf_casting",
  "methods.trolling",
];

const SPECIES_OPTIONS = [
  "species.albacore_tuna",
  "species.arapaima",
  "species.atlantic_bluefin_tuna",
  "species.atlantic_bonito",
  "species.atlantic_mackerel",
  "species.atlantic_salmon",
  "species.atlantic_tarpon",
  "species.barracuda_european",
  "species.barramundi",
  "species.blacktip_shark",
  "species.bluefish",
  "species.bonefish",
  "species.brown_trout",
  "species.channel_catfish",
  "species.common_bream",
  "species.common_carp",
  "species.common_roach",
  "species.dusky_grouper",
  "species.european_chub",
  "species.european_perch",
  "species.european_sea_bass",
  "species.grass_carp",
  "species.greater_amberjack",
  "species.largemouth_bass",
  "species.mahi_mahi",
  "species.mirror_carp",
  "species.northern_pike",
  "species.peacock_bass",
  "species.pickhandle_barracuda",
  "species.queenfish",
  "species.rainbow_trout",
  "species.red_snapper",
  "species.skipjack_tuna",
  "species.smallmouth_bass",
  "species.tench",
  "species.trevally",
  "species.wahoo",
  "species.wels_catfish",
  "species.yellowfin_tuna",
  "species.zander",
];

function normalizeText(value) {
  if (typeof value === "string") {
    return value.trim();
  }

  if (value === null || value === undefined) {
    return "";
  }

  return String(value).trim();
}

function normalizeList(values) {
  if (!Array.isArray(values)) {
    return [];
  }

  return values
    .map((value) => normalizeText(value))
    .filter(Boolean);
}

function normalizeWaterTypeSelection(value) {
  const normalized = normalizeText(value);
  return ["freshwater", "saltwater", "both"].includes(normalized)
    ? normalized
    : "";
}

function humanizeAccountType(t, value) {
  const normalized = normalizeText(value);
  if (!normalized) {
    return t("profile_bio_edit_not_set", {
      defaultValue: "Not set",
    });
  }

  return t(normalized, {
    defaultValue: normalized.charAt(0).toUpperCase() + normalized.slice(1),
  });
}

function toggleListValue(value, setValues) {
  setValues((previousValues) =>
    previousValues.includes(value)
      ? previousValues.filter((item) => item !== value)
      : [...previousValues, value]
  );
}

const USERNAME_PATTERN = /^[A-Za-z0-9._-]{3,20}$/;

function usernameValidationKey(username) {
  if (username.length < 3) return "username_too_short";
  if (username.length > 20) return "username_too_long";
  if (!USERNAME_PATTERN.test(username)) return "username_invalid_chars";
  return "";
}

function usernameErrorCode(payload) {
  if (typeof payload?.detail === "string") return payload.detail;
  if (payload?.detail && typeof payload.detail === "object") {
    return payload.detail.code || "";
  }
  return "";
}

export default function ProfileBioEditModal({
  profileDetails,
  onClose,
  onSaved,
  onUsernameSaved,
}) {
  const { t } = useTranslation();
  const accountType = normalizeText(profileDetails?.accountType);
  const isPersonalAccount = accountType === "personal";
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [experience, setExperience] = useState("");
  const [waterType, setWaterType] = useState("all");
  const [fishingStyle, setFishingStyle] = useState("");
  const [rangeType, setRangeType] = useState("");
  const [selectedMethods, setSelectedMethods] = useState([]);
  const [selectedSpecies, setSelectedSpecies] = useState([]);
  const [bio, setBio] = useState("");
  const [instagram, setInstagram] = useState("");
  const [youtube, setYoutube] = useState("");
  const [facebook, setFacebook] = useState("");
  const [website, setWebsite] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [username, setUsername] = useState("");
  const [savedUsername, setSavedUsername] = useState("");
  const [usernameAvailability, setUsernameAvailability] = useState("idle");
  const [usernameMessage, setUsernameMessage] = useState("");
  const [isUsernameSaving, setIsUsernameSaving] = useState(false);
  const [usernameNextAllowedAt, setUsernameNextAllowedAt] = useState("");
  const hydratedProfileIdRef = useRef(null);
  const usernameRequestRef = useRef(0);

  useEffect(() => {
    const profileId = normalizeText(profileDetails?.id) || "__profile_without_id__";
    if (hydratedProfileIdRef.current === profileId) {
      return;
    }
    hydratedProfileIdRef.current = profileId;

    const normalizedSocial =
      profileDetails?.social && typeof profileDetails.social === "object"
        ? profileDetails.social
        : {};

    setCountry(normalizeText(profileDetails?.country));
    setCity(normalizeText(profileDetails?.city));
    setExperience(normalizeText(profileDetails?.experience));
    setWaterType(normalizeWaterTypeSelection(profileDetails?.waterType));
    setFishingStyle(normalizeText(profileDetails?.fishingStyle));
    setRangeType(normalizeText(profileDetails?.rangeType));
    setSelectedMethods(normalizeList(profileDetails?.methods));
    setSelectedSpecies(normalizeList(profileDetails?.favoriteSpecies));
    setBio(normalizeText(profileDetails?.bio));
    setInstagram(normalizeText(normalizedSocial.instagram));
    setYoutube(normalizeText(normalizedSocial.youtube));
    setFacebook(normalizeText(normalizedSocial.facebook));
    setWebsite(normalizeText(normalizedSocial.website));
    const initialUsername = normalizeText(profileDetails?.username);
    setUsername(initialUsername);
    setSavedUsername(initialUsername);
    setUsernameNextAllowedAt(normalizeText(profileDetails?.usernameChangedAt));
    setUsernameAvailability("idle");
    setUsernameMessage("");
    setErrorMessage("");
    setIsSaving(false);
  }, [profileDetails]);

  const normalizedUsername = normalizeText(username);
  const usernameValidationError = usernameValidationKey(normalizedUsername);
  const usernameChanged = normalizedUsername !== savedUsername;
  const nextAllowedDate = usernameNextAllowedAt
    ? new Date(usernameNextAllowedAt)
    : null;
  const cooldownUntil =
    nextAllowedDate && Number.isFinite(nextAllowedDate.getTime())
      ? new Date(nextAllowedDate.getTime() + 30 * 24 * 60 * 60 * 1000)
      : null;
  const usernameCooldownActive = Boolean(
    cooldownUntil && cooldownUntil.getTime() > Date.now()
  );

  useEffect(() => {
    if (!usernameChanged || usernameValidationError || usernameCooldownActive) {
      setUsernameAvailability("idle");
      return undefined;
    }

    const requestId = usernameRequestRef.current + 1;
    usernameRequestRef.current = requestId;
    const controller = new AbortController();
    setUsernameAvailability("checking");
    setUsernameMessage("");

    const timeoutId = window.setTimeout(async () => {
      const token = getActiveAwareAuthToken(window);
      try {
        const response = await fetch(
          `${API_BASE}/auth/username-availability?username=${encodeURIComponent(
            normalizedUsername
          )}`,
          withApiCredentials({
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            signal: controller.signal,
          })
        );
        const data = await response.json().catch(() => ({}));
        if (requestId !== usernameRequestRef.current) return;
        if (!response.ok) {
          setUsernameAvailability("error");
          return;
        }
        setUsernameAvailability(data?.available ? "available" : "unavailable");
      } catch (error) {
        if (error?.name === "AbortError") return;
        if (requestId === usernameRequestRef.current) {
          setUsernameAvailability("error");
        }
      }
    }, 400);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [normalizedUsername, usernameChanged, usernameCooldownActive, usernameValidationError]);

  const handleUsernameChange = (event) => {
    setUsername(event.target.value);
    setUsernameMessage("");
  };

  const handleUsernameSave = async () => {
    if (
      !usernameChanged ||
      usernameValidationError ||
      usernameAvailability !== "available" ||
      usernameCooldownActive ||
      isUsernameSaving ||
      isSaving
    ) {
      return;
    }

    const token = getActiveAwareAuthToken(window);
    setIsUsernameSaving(true);
    setUsernameMessage("");
    try {
      const response = await fetch(
        `${API_BASE}/auth/update-username`,
        withApiCredentials({
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ username: normalizedUsername }),
        })
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const code = usernameErrorCode(data);
        if (code === "username_already_taken") {
          setUsernameAvailability("unavailable");
        }
        if (code === "username_change_cooldown") {
          const nextAllowed = new Date(data?.detail?.nextAllowedAt || "");
          setUsernameNextAllowedAt(
            Number.isFinite(nextAllowed.getTime())
              ? new Date(
                  nextAllowed.getTime() - 30 * 24 * 60 * 60 * 1000
                ).toISOString()
              : ""
          );
        }
        const messageKey = code || "username_change_unavailable";
        setUsernameMessage(
          t(messageKey, {
            defaultValue:
              code === "reauth_required"
                ? "For security, sign out and sign in again before changing your username."
                : code === "username_change_cooldown"
                ? "You can change your username only once every 30 days."
                : code === "username_already_taken"
                ? "That username is already taken."
                : "Unable to change your username right now.",
          })
        );
        return;
      }

      const protectedData = mergeUserPreservingNewerUsername(
        {
          ...profileDetails,
          username: savedUsername,
          usernameChangedAt: usernameNextAllowedAt,
        },
        data
      );
      const updatedUsername =
        normalizeText(protectedData?.username) || normalizedUsername;
      setUsername(updatedUsername);
      setSavedUsername(updatedUsername);
      setUsernameNextAllowedAt(normalizeText(protectedData?.usernameChangedAt));
      setUsernameAvailability("idle");
      setUsernameMessage(
        t("username_change_success", {
          defaultValue: "Username updated successfully.",
        })
      );
      window.dispatchEvent(
        new CustomEvent("userUpdated", { detail: protectedData })
      );
      if (typeof onUsernameSaved === "function") onUsernameSaved(protectedData);
    } catch (error) {
      console.error("username update failed");
      setUsernameMessage(
        t("username_change_unavailable", {
          defaultValue: "Unable to change your username right now.",
        })
      );
    } finally {
      setIsUsernameSaving(false);
    }
  };

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (isSaving || isUsernameSaving) return;
    onClose();
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (!isSaving && !isUsernameSaving) onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSaving, isUsernameSaving, onClose]);

  const sortedMethods = [...FISHING_METHODS].sort((left, right) =>
    t(left).localeCompare(t(right))
  );
  const sortedSpecies = [...SPECIES_OPTIONS].sort((left, right) =>
    t(left).localeCompare(t(right))
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSaving || isUsernameSaving) return;

    const normalizedCountry = normalizeText(country);
    const normalizedCity = normalizeText(city);

    if (!normalizedCountry) {
      setErrorMessage(
        t("country_required", {
          defaultValue: "Country is required.",
        })
      );
      return;
    }

    if (!normalizedCity) {
      setErrorMessage(
        t("city_required", {
          defaultValue: "City is required.",
        })
      );
      return;
    }

    const token = getActiveAwareAuthToken(window);
    const hasSessionUser = Boolean(profileDetails?.id);
    if (!token && !hasSessionUser) {
      setErrorMessage(
        t("profile_bio_edit_auth_error", {
          defaultValue: "You need to sign in again before updating your profile details.",
        })
      );
      return;
    }

    setIsSaving(true);
    setErrorMessage("");

    try {
      const res = await fetch(
        `${API_BASE}/auth/update-details`,
        withApiCredentials({
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            bio: normalizeText(bio),
            country: normalizedCountry,
            city: normalizedCity,
            experience: isPersonalAccount ? normalizeText(experience) : "",
            waterType: isPersonalAccount
              ? normalizeText(waterType) || null
              : null,
            fishingStyle: isPersonalAccount ? normalizeText(fishingStyle) : "",
            rangeType: isPersonalAccount ? normalizeText(rangeType) : "",
            methods: isPersonalAccount ? selectedMethods : [],
            favoriteSpecies: isPersonalAccount ? selectedSpecies : [],
            social: {
              instagram: normalizeText(instagram),
              youtube: normalizeText(youtube),
              facebook: normalizeText(facebook),
              website: normalizeText(website),
            },
          }),
        })
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setErrorMessage(
          t(data?.detail || "profile_bio_edit_save_error", {
            defaultValue: "Unable to update your profile details right now.",
          })
        );
        return;
      }

      const protectedData = mergeUserPreservingNewerUsername(
        {
          ...profileDetails,
          username: savedUsername,
          usernameChangedAt: usernameNextAllowedAt,
        },
        data
      );
      setUsername(normalizeText(protectedData?.username));
      setSavedUsername(normalizeText(protectedData?.username));
      setUsernameNextAllowedAt(
        normalizeText(protectedData?.usernameChangedAt)
      );
      window.dispatchEvent(
        new CustomEvent("userUpdated", { detail: protectedData })
      );
      trackProductEvent("profile_updated", {
        featureArea: "profile",
        metadata: {
          source: "edit_bio",
          account_type: accountType || "",
          has_country: Boolean(normalizedCountry),
          has_city: Boolean(normalizedCity),
          has_optional_personal_details: Boolean(
            normalizeText(experience) ||
              normalizeText(fishingStyle) ||
              normalizeText(rangeType) ||
              selectedMethods.length ||
              selectedSpecies.length
          ),
          has_social_links: Boolean(
            normalizeText(instagram) ||
              normalizeText(youtube) ||
              normalizeText(facebook) ||
              normalizeText(website)
          ),
        },
      });
      if (typeof onSaved === "function") {
        onSaved(protectedData);
      }
    } catch (error) {
      console.error("profile bio update failed", error);
      setErrorMessage(
        t("profile_bio_edit_save_error", {
          defaultValue: "Unable to update your profile details right now.",
        })
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className="profile-progression-modal-overlay"
      onClick={handleClose}
    >
      <div
        className="profile-progression-modal profile-progression-modal--bio profile-progression-modal--edit-bio"
        role="dialog"
        aria-modal="true"
        aria-label={t("profile_bio_edit_dialog_label", {
          defaultValue: "Update profile details",
        })}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="profile-progression-modal-header-actions">
          <button
            type="button"
            className="profile-progression-modal-close"
            onClick={handleClose}
          >
            {t("progression_milestone_popup_close", {
              defaultValue: "Close",
            })}
          </button>
        </div>

        <div className="profile-progression-modal-kicker profile-progression-modal-kicker--bio">
          {t("profile_bio_edit_kicker", {
            defaultValue: "Update Bio",
          })}
        </div>

        <p className="profile-progression-modal-body profile-progression-edit-intro">
          {t("profile_bio_edit_intro", {
            defaultValue:
              "Edit the profile details shown in your bio and interests view. Optional fields can be cleared at any time.",
          })}
        </p>

        <form
          className="profile-progression-edit-form"
          onSubmit={handleSubmit}
        >
          <section className="profile-progression-edit-section profile-progression-edit-section--username">
            <div className="profile-progression-edit-section-label">
              {t("username_section_title", { defaultValue: "Username" })}
            </div>
            <p id="profile-username-help" className="profile-progression-edit-help">
              {t("username_public_password_warning", {
                defaultValue:
                  "Your username is public. Never use a password as your username.",
              })}
            </p>
            <p className="profile-progression-edit-help">
              {t("username_allowed_characters", {
                defaultValue:
                  "Use 3–20 ASCII letters, numbers, periods (.), underscores (_), or hyphens (-).",
              })}
            </p>
            <div className="profile-progression-edit-username-row">
              <label className="profile-progression-edit-field profile-progression-edit-field--wide">
                <span className="profile-progression-edit-label">
                  {t("username", { defaultValue: "Username" })}
                </span>
                <input
                  id="profile-username"
                  name="username"
                  autoComplete="username"
                  className="profile-progression-edit-input"
                  type="text"
                  value={username}
                  onChange={handleUsernameChange}
                  maxLength={21}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleUsernameSave();
                    }
                  }}
                  aria-describedby="profile-username-help profile-username-status"
                  aria-invalid={Boolean(usernameChanged && usernameValidationError)}
                />
              </label>
              <span className="profile-progression-edit-counter" aria-live="polite">
                {username.length}/20
              </span>
              <button
                type="button"
                className="profile-progression-edit-action profile-progression-edit-action--primary"
                onClick={handleUsernameSave}
                disabled={
                  !usernameChanged ||
                  Boolean(usernameValidationError) ||
                  usernameAvailability !== "available" ||
                  usernameCooldownActive ||
                  isUsernameSaving ||
                  isSaving
                }
              >
                {isUsernameSaving
                  ? t("saving", { defaultValue: "Saving..." })
                  : t("username_save_button", { defaultValue: "Change username" })}
              </button>
            </div>
            <div
              id="profile-username-status"
              className="profile-progression-edit-username-status"
              role={usernameValidationError || usernameMessage ? "alert" : "status"}
              aria-live="polite"
            >
              {usernameMessage
                ? usernameMessage
                : usernameChanged && usernameValidationError
                ? t(usernameValidationError)
                : usernameCooldownActive
                ? t("username_change_cooldown_until", {
                    defaultValue: "You can change your username again on {{date}}.",
                    date: cooldownUntil.toLocaleDateString(),
                  })
                : usernameAvailability === "checking"
                ? t("username_checking", { defaultValue: "Checking availability…" })
                : usernameAvailability === "available"
                ? t("username_available", { defaultValue: "Username is available." })
                : usernameAvailability === "unavailable"
                ? t("username_already_taken", { defaultValue: "That username is already taken." })
                : usernameAvailability === "error"
                ? t("username_check_unavailable", {
                    defaultValue: "Unable to check username availability right now.",
                  })
                : ""}
            </div>
          </section>

          <section className="profile-progression-edit-section">
            <div className="profile-progression-edit-section-label">
              {t("profile_bio_edit_required_label", {
                defaultValue: "Profile details",
              })}
            </div>

            <div className="profile-progression-edit-grid">
              <label className="profile-progression-edit-field">
                <span className="profile-progression-edit-label">
                  {t("profile_bio_country_label", {
                    defaultValue: "Country",
                  })}{" "}
                  *
                </span>
                <input
                  className="profile-progression-edit-input"
                  type="text"
                  value={country}
                  onChange={(event) => setCountry(event.target.value)}
                  list="profile-bio-country-options"
                  autoComplete="country-name"
                />
              </label>

              <label className="profile-progression-edit-field">
                <span className="profile-progression-edit-label">
                  {t("profile_bio_city_label", {
                    defaultValue: "City",
                  })}{" "}
                  *
                </span>
                <input
                  className="profile-progression-edit-input"
                  type="text"
                  value={city}
                  onChange={(event) => setCity(event.target.value)}
                  autoComplete="address-level2"
                />
              </label>

              <div className="profile-progression-edit-field">
                <span className="profile-progression-edit-label">
                  {t("profile_bio_account_type_label", {
                    defaultValue: "Account type",
                  })}
                </span>
                <div className="profile-progression-edit-readonly">
                  {humanizeAccountType(t, accountType)}
                </div>
              </div>

              {isPersonalAccount ? (
                <>
                  <label className="profile-progression-edit-field">
                    <span className="profile-progression-edit-label">
                      {t("profile_bio_water_type_label", {
                        defaultValue: "Water type",
                      })}
                    </span>
                    <select
                      className="profile-progression-edit-select"
                      value={waterType}
                      onChange={(event) => setWaterType(event.target.value)}
                    >
                      <option value="">
                        {t("profile_bio_edit_not_set", {
                          defaultValue: "Not set",
                        })}
                      </option>
                      <option value="both">
                        {t("both", { defaultValue: "Both" })}
                      </option>
                      <option value="freshwater">
                        {t("freshwater", { defaultValue: "Freshwater" })}
                      </option>
                      <option value="saltwater">
                        {t("saltwater", {
                          defaultValue: "Salt water (marine)",
                        })}
                      </option>
                    </select>
                  </label>

                  <label className="profile-progression-edit-field">
                    <span className="profile-progression-edit-label">
                      {t("profile_bio_fishing_style_label", {
                        defaultValue: "Way of Fishing",
                      })}
                    </span>
                    <select
                      className="profile-progression-edit-select"
                      value={fishingStyle}
                      onChange={(event) => setFishingStyle(event.target.value)}
                    >
                      <option value="">
                        {t("select_way_of_fishing", {
                          defaultValue: "Select Way of Fishing",
                        })}
                      </option>
                      <option value="shore">
                        {t("shore", { defaultValue: "Shore" })}
                      </option>
                      <option value="boat">
                        {t("boat", { defaultValue: "Boat" })}
                      </option>
                      <option value="both">
                        {t("both", { defaultValue: "Both" })}
                      </option>
                    </select>
                  </label>

                  <label className="profile-progression-edit-field">
                    <span className="profile-progression-edit-label">
                      {t("profile_bio_range_type_label", {
                        defaultValue: "Fishing range",
                      })}
                    </span>
                    <select
                      className="profile-progression-edit-select"
                      value={rangeType}
                      onChange={(event) => setRangeType(event.target.value)}
                    >
                      <option value="">
                        {t("select_range_type", {
                          defaultValue: "Select fishing range",
                        })}
                      </option>
                      <option value="local">
                        {t("local", { defaultValue: "Local" })}
                      </option>
                      <option value="international">
                        {t("international", {
                          defaultValue: "International",
                        })}
                      </option>
                      <option value="both">
                        {t("both", { defaultValue: "Both" })}
                      </option>
                    </select>
                  </label>
                </>
              ) : null}
            </div>
          </section>

          <section className="profile-progression-edit-section profile-progression-edit-section--optional">
            <div className="profile-progression-edit-section-label">
              {t("profile_bio_edit_optional_label", {
                defaultValue: "Optional details",
              })}
            </div>

            <div className="profile-progression-edit-grid">
              {isPersonalAccount ? (
                <label className="profile-progression-edit-field">
                  <span className="profile-progression-edit-label">
                    {t("profile_bio_experience_label", {
                      defaultValue: "Experience",
                    })}
                  </span>
                  <select
                    className="profile-progression-edit-select"
                    value={experience}
                    onChange={(event) => setExperience(event.target.value)}
                  >
                    <option value="">
                      {t("profile_bio_edit_optional_placeholder", {
                        defaultValue: "Not set",
                      })}
                    </option>
                    <option value="beginner">
                      {t("beginner", { defaultValue: "Beginner" })}
                    </option>
                    <option value="intermediate">
                      {t("intermediate", {
                        defaultValue: "Intermediate",
                      })}
                    </option>
                    <option value="expert">
                      {t("expert", { defaultValue: "Expert" })}
                    </option>
                    <option value="professional">
                      {t("professional", {
                        defaultValue: "Professional",
                      })}
                    </option>
                  </select>
                </label>
              ) : null}

              <label className="profile-progression-edit-field profile-progression-edit-field--wide">
                <span className="profile-progression-edit-label">
                  {t("profile_bio_text_label", {
                    defaultValue: "Bio",
                  })}
                </span>
                <textarea
                  className="profile-progression-edit-textarea"
                  value={bio}
                  onChange={(event) => setBio(event.target.value)}
                  rows={4}
                />
              </label>

              {isPersonalAccount ? (
                <>
                  <div className="profile-progression-edit-field profile-progression-edit-field--wide">
                    <span className="profile-progression-edit-label">
                      {t("profile_bio_methods_label", {
                        defaultValue: "Type of Fishing",
                      })}
                    </span>
                    <div className="profile-progression-edit-checkbox-panel">
                      <div className="profile-progression-edit-checkbox-list">
                        {sortedMethods.map((method) => (
                          <label
                            key={method}
                            className="profile-progression-edit-checkbox-item"
                          >
                            <input
                              type="checkbox"
                              checked={selectedMethods.includes(method)}
                              onChange={() =>
                                toggleListValue(method, setSelectedMethods)
                              }
                            />
                            <span>{t(method)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="profile-progression-edit-field profile-progression-edit-field--wide">
                    <span className="profile-progression-edit-label">
                      {t("profile_bio_species_label", {
                        defaultValue: "Favorite species",
                      })}
                    </span>
                    <div className="profile-progression-edit-checkbox-panel">
                      <div className="profile-progression-edit-checkbox-list">
                        {sortedSpecies.map((species) => (
                          <label
                            key={species}
                            className="profile-progression-edit-checkbox-item"
                          >
                            <input
                              type="checkbox"
                              checked={selectedSpecies.includes(species)}
                              onChange={() =>
                                toggleListValue(species, setSelectedSpecies)
                              }
                            />
                            <span>{t(species)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              ) : null}

              <label className="profile-progression-edit-field">
                <span className="profile-progression-edit-label">
                  {t("profile_bio_instagram_label", {
                    defaultValue: "Instagram URL",
                  })}
                </span>
                <input
                  className="profile-progression-edit-input"
                  type="url"
                  value={instagram}
                  onChange={(event) => setInstagram(event.target.value)}
                  placeholder="https://instagram.com/..."
                />
              </label>

              <label className="profile-progression-edit-field">
                <span className="profile-progression-edit-label">
                  {t("profile_bio_youtube_label", {
                    defaultValue: "YouTube URL",
                  })}
                </span>
                <input
                  className="profile-progression-edit-input"
                  type="url"
                  value={youtube}
                  onChange={(event) => setYoutube(event.target.value)}
                  placeholder="https://youtube.com/..."
                />
              </label>

              <label className="profile-progression-edit-field">
                <span className="profile-progression-edit-label">
                  {t("profile_bio_facebook_label", {
                    defaultValue: "Facebook URL",
                  })}
                </span>
                <input
                  className="profile-progression-edit-input"
                  type="url"
                  value={facebook}
                  onChange={(event) => setFacebook(event.target.value)}
                  placeholder="https://facebook.com/..."
                />
              </label>

              <label className="profile-progression-edit-field">
                <span className="profile-progression-edit-label">
                  {t("profile_bio_website_label", {
                    defaultValue: "Website",
                  })}
                </span>
                <input
                  className="profile-progression-edit-input"
                  type="url"
                  value={website}
                  onChange={(event) => setWebsite(event.target.value)}
                  placeholder="https://..."
                />
              </label>
            </div>
          </section>

          {errorMessage ? (
            <div className="profile-progression-edit-error">
              {errorMessage}
            </div>
          ) : null}

          <div className="profile-progression-edit-actions">
            <button
              type="button"
              className="profile-progression-edit-action profile-progression-edit-action--secondary"
              onClick={handleClose}
              disabled={isSaving || isUsernameSaving}
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              type="submit"
              className="profile-progression-edit-action profile-progression-edit-action--primary"
              disabled={isSaving || isUsernameSaving}
            >
              {isSaving
                ? t("saving", { defaultValue: "Saving..." })
                : t("save_changes", { defaultValue: "Save changes" })}
            </button>
          </div>
        </form>

        <datalist id="profile-bio-country-options">
          {countries.map((countryName) => (
            <option key={countryName} value={countryName} />
          ))}
        </datalist>
      </div>
    </div>
  );
}
